import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const FooterContainer = styled.footer`
  background-color: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.sectionGap || props.theme.spacing['3xl']} ${props => props.theme.spacing.containerPadding || props.theme.spacing['3xl']};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.md};
  }
`;

const FooterContent = styled.div`
  max-width: ${props => props.theme.layout.containerMaxWidth};
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr); /* Five columns */
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const SectionHeading = styled.h3<{ $isOpen?: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.sm}; /* Small font */
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  color: ${props => props.theme.colors.white} !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  opacity: 1 !important;
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    cursor: default;
  }
  
  &::after {
    content: '${props => props.$isOpen ? '−' : '+'}';
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.normal};
    display: block;
    
    @media (min-width: ${props => props.theme.breakpoints.tablet}) {
      display: none;
    }
  }
`;

const SectionContent = styled(motion.div)`
  overflow: hidden;
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    display: block !important;
  }
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.colors.white} !important;
  text-decoration: none;
  font-size: 0.875rem; /* 12-14px, using 14px */
  font-weight: ${props => props.theme.typography.fontWeight.normal}; /* Regular weight */
  line-height: 1.5; /* Single-spaced */
  display: block;
  margin-bottom: ${props => props.theme.spacing.xs};
  transition: opacity ${props => props.theme.transitions.base};
  opacity: 1 !important;
  
  &:hover {
    text-decoration: underline; /* Hover state has underline */
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
`;

const SocialLink = styled.a`
  color: ${props => props.theme.colors.white} !important;
  text-decoration: none;
  opacity: 0.9 !important;
  transition: opacity ${props => props.theme.transitions.base};
  
  &:hover {
    opacity: 1 !important;
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const FooterBottom = styled.div`
  max-width: ${props => props.theme.layout.containerMaxWidth};
  margin: 0 auto;
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.white} !important;
  opacity: 0.9 !important;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
    text-align: center;
  }
`;

const CopyrightLink = styled.a`
  color: ${props => props.theme.colors.white} !important;
  text-decoration: underline;
  opacity: 1 !important;
  
  &:hover {
    opacity: 0.9 !important;
  }
`;

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const NewFooter: React.FC = () => {
  const { t } = useTranslation();
  // All sections are closed by default, open on click
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <SectionHeading 
            $isOpen={openSections['join']}
            onClick={() => toggleSection('join')}
          >
            {t('footer.helpUs')}
          </SectionHeading>
          <SectionContent
            initial={false}
            animate={{ 
              height: openSections['join'] === true ? 'auto' : 0, 
              opacity: openSections['join'] === true ? 1 : 0 
            }}
            transition={{ duration: 0.3 }}
          >
            <FooterLink to="/pages/faqs">{t('footer.faqs')}</FooterLink>
            <FooterLink to="/pages/how-to-shop">{t('footer.howToShop')}</FooterLink>
          </SectionContent>
        </FooterSection>

        <FooterSection>
          <SectionHeading 
            $isOpen={openSections['contact']}
            onClick={() => toggleSection('contact')}
          >
            {t('footer.contactUs')}
          </SectionHeading>
          <SectionContent
            initial={false}
            animate={{ 
              height: openSections['contact'] === true ? 'auto' : 0, 
              opacity: openSections['contact'] === true ? 1 : 0 
            }}
            transition={{ duration: 0.3 }}
          >
            <FooterLink to="/pages/contact-us">{t('footer.contactLink')}</FooterLink>
          </SectionContent>
        </FooterSection>

        <FooterSection>
          <SectionHeading 
            $isOpen={openSections['know']}
            onClick={() => toggleSection('know')}
          >
            {t('footer.knowUs')}
          </SectionHeading>
          <SectionContent
            initial={false}
            animate={{ 
              height: openSections['know'] === true ? 'auto' : 0, 
              opacity: openSections['know'] === true ? 1 : 0 
            }}
            transition={{ duration: 0.3 }}
          >
            <FooterLink to="/pages/about-us">{t('footer.aboutUs')}</FooterLink>
            <FooterLink to="/pages/how-to-shop">{t('footer.howToShop')}</FooterLink>
            <FooterLink to="/pages/faqs">{t('footer.faqs')}</FooterLink>
            <FooterLink to="/policies/terms-of-service">{t('footer.termsOfService')}</FooterLink>
            <FooterLink to="/pages/brands">{t('footer.azCatalog')}</FooterLink>
          </SectionContent>
        </FooterSection>

        <FooterSection>
          <SectionHeading 
            $isOpen={openSections['policies']}
            onClick={() => toggleSection('policies')}
          >
            {t('footer.policy')}
          </SectionHeading>
          <SectionContent
            initial={false}
            animate={{ 
              height: openSections['policies'] === true ? 'auto' : 0, 
              opacity: openSections['policies'] === true ? 1 : 0 
            }}
            transition={{ duration: 0.3 }}
          >
            <FooterLink to="/policies/terms-of-service">{t('footer.termsConditions')}</FooterLink>
            <FooterLink to="/pages/delivery">{t('footer.deliveryPolicy')}</FooterLink>
            <FooterLink to="/returns">{t('footer.requestReturn')}</FooterLink>
            <FooterLink to="/returns-policy">{t('footer.returnsPolicy')}</FooterLink>
            <FooterLink to="/policies/refund-policy">{t('footer.refundPolicy')}</FooterLink>
            <FooterLink to="/pages/privacy-policy">{t('footer.privacyPolicy')}</FooterLink>
            <FooterLink to="/pages/cookies-policy">{t('footer.cookiesPolicy')}</FooterLink>
            <FooterLink to="/pages/order-cancellation">{t('footer.orderCancellation')}</FooterLink>
          </SectionContent>
        </FooterSection>

        <FooterSection>
          <SectionHeading 
            $isOpen={openSections['social']}
            onClick={() => toggleSection('social')}
          >
            {t('footer.followUs')}
          </SectionHeading>
          <SectionContent
            initial={false}
            animate={{ 
              height: openSections['social'] === true ? 'auto' : 0, 
              opacity: openSections['social'] === true ? 1 : 0 
            }}
            transition={{ duration: 0.3 }}
          >
            <SocialLinks>
              <SocialLink href="https://www.facebook.com/seenoty" target="_blank" rel="noopener noreferrer" aria-label={t('footer.facebook')}>
                <FacebookIcon />
              </SocialLink>
              <SocialLink href="https://www.instagram.com/seenoty" target="_blank" rel="noopener noreferrer" aria-label={t('footer.instagram')}>
                <InstagramIcon />
              </SocialLink>
              <SocialLink href="https://www.youtube.com/seenoty" target="_blank" rel="noopener noreferrer" aria-label={t('footer.youtube')}>
                <YouTubeIcon />
              </SocialLink>
            </SocialLinks>
          </SectionContent>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <div>{t('footer.rights')}</div>
        <div>
          <CopyrightLink href="" target="_blank" rel="noopener noreferrer">
            SEENOTY
          </CopyrightLink>
          {' '}
        </div>
      </FooterBottom>
    </FooterContainer>
  );
};

export default NewFooter;


